import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Localization from 'expo-localization';
import i18n, { use as loadPlugin } from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';

enum SupportedLanguage {
  en = 'en',
  fr = 'fr',
  es = 'es',
}

const languageConfigs: Record<SupportedLanguage, { name: string }> = {
  en: { name: 'English' },
  fr: { name: 'Français' },
  es: { name: 'Español' },
};

const supportedLanguages = Object.values(SupportedLanguage);

const LANGUAGE_KEY = 'user_language';

export async function getStoredLanguage(): Promise<SupportedLanguage | null> {
  try {
    const stored = await AsyncStorage.getItem(LANGUAGE_KEY);
    if (stored && supportedLanguages.includes(stored as SupportedLanguage)) {
      return stored as SupportedLanguage;
    }
  } catch (error) {
    console.error('Error reading stored language:', error);
  }
  return null;
}

export async function setStoredLanguage(
  lang: SupportedLanguage,
): Promise<void> {
  try {
    await AsyncStorage.setItem(LANGUAGE_KEY, lang);
  } catch (error) {
    console.error('Error storing language:', error);
  }
}

export async function initI18n(): Promise<void> {
  let language: SupportedLanguage = SupportedLanguage.en;

  // Try to get stored language
  const storedLanguage = await getStoredLanguage();
  if (storedLanguage) {
    language = storedLanguage;
  } else {
    // Detect device language
    const locales = Localization.getLocales();
    const firstLocalLanguageCode = locales?.[0]?.languageCode;

    if (
      firstLocalLanguageCode &&
      supportedLanguages.includes(firstLocalLanguageCode as SupportedLanguage)
    ) {
      language = firstLocalLanguageCode as SupportedLanguage;
    }
  }

  loadPlugin(initReactI18next).init({
    resources: {
      en: { translation: en },
      fr: { translation: fr },
      es: { translation: es },
    },
    lng: language,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // React already escapes values
    },
  });
}

export default i18n;
export { SupportedLanguage, languageConfigs, supportedLanguages };
